import React, { useEffect, useState } from "react";

import {
  ErrorText,
  IconWrapper,
  InputLabel,
  StyledInput,
  StyledInputContainer,
  StyledInputWrapper,
} from "./styles";
import { InputProps } from "./types";

/**
 * Componente Input
 *
 * Um campo de entrada customizável com suporte para rótulos, ícones, mensagens de erro e diversos estilos.
 *
 * @param {string} [label] - Texto do rótulo exibido acima do campo de entrada.
 * @param {string} [placeholder] - Texto exibido como placeholder no campo.
 * @param {'text' | 'password' | 'email' | 'number'} [type='text'] - Tipo do campo de entrada.
 * @param {React.ReactNode} [icon] - Ícone exibido dentro do campo.
 * @param {string} [error] - Mensagem de erro exibida abaixo do campo.
 * @param {boolean} [disabled=false] - Define se o campo está desativado.
 * @param {'small' | 'medium' | 'large'} [size='medium'] - Tamanho do campo.
 * @param {boolean} [fullWidth=false] - Define se o campo ocupa 100% da largura disponível.
 * @param {'default' | 'styled'} [variant='default'] - Estilo do campo (borda simples ou com sombra).
 * @param {string} [value] - Valor do campo.
 * @param {keyof typeof theme.colors.font} [textColor='darkGray'] - Cor do texto inserido no campo.
 * @param {keyof typeof theme.colors.neutral | keyof typeof theme.colors.primary} [borderColor='white'] - Cor da borda do campo.
 * @param {keyof typeof theme.colors.neutral | keyof typeof theme.colors.background} [backgroundColor='transparent'] - Cor de fundo do campo.
 * @param {keyof typeof theme.colors.neutral | keyof typeof theme.colors.font} [placeholderColor='mediumGray'] - Cor do texto do placeholder.
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - Callback executado ao alterar o valor do campo.
 * @param {() => void} [onFocus] - Callback executado ao focar no campo.
 * @param {React.LegacyRef<HTMLInputElement>} ref - Ref
 */
export const Input: React.FC<InputProps> = ({
  label,
  placeholder,
  type = "text",
  icon,
  error,
  disabled = false,
  size = "medium",
  fullWidth = false,
  variant = "default",
  value,
  borderColor = "white",
  textColor = "mediumGray",
  placeholderColor = "mediumGray",
  backgroundColor = "transparent",
  onChange,
  onFocus,
  ref,
  isFakeFocus = false,
}) => {
  const inputId = `input-${label?.replace(/\s+/g, "-").toLowerCase()}`;
  const [showFakeCursor, setShowFakeCursor] = useState(isFakeFocus);

  useEffect(() => {
    setShowFakeCursor(isFakeFocus);
  }, [isFakeFocus]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setShowFakeCursor(false);
    onFocus?.();
  };

  return (
    <StyledInputWrapper fullWidth={fullWidth}>
      {label && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
      <StyledInputContainer
        hasIcon={!!icon}
        isError={!!error}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        disabled={disabled}
        variant={variant}
        size={size}
        isFakeFocus={showFakeCursor}
      >
        {icon && <IconWrapper size={size}>{icon}</IconWrapper>}
        <StyledInput
          id={inputId}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          textColor={textColor}
          placeholderColor={placeholderColor}
          hasIcon={!!icon}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          ref={ref}
          autoComplete="off"
        />
      </StyledInputContainer>
      {error && <ErrorText id={`${inputId}-error`}>{error}</ErrorText>}
    </StyledInputWrapper>
  );
};
